import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { GrasshopperSim } from '../sim/Grasshopper';
import type { Terrain } from '../game/Terrain';
import { readTune } from '../ui/TuneStore';

import { assetUrl as _assetUrl } from '../ui/AssetMap';
const MODEL_SLOT = 'grasshopper';
const MODEL_URL_DEFAULT = '/assets/grasshopper-v6.glb';
const HOPPER_LENGTH = 0.15;
const TUNE_ID = 'grasshopper';
const TUNE_DEFAULTS = { scale: 0.302, rotX: 0, rotY: 0, rotZ: 0, offY: 0 };

interface Avatar {
  root: THREE.Group;
  mixer: THREE.AnimationMixer;
  action: THREE.AnimationAction | null;
}

/** Animated grasshoppers rendered from the Sketchfab GLB. One cloned skinned
 *  mesh per sim hopper, driven by its own AnimationMixer. */
export class GrasshopperMesh {
  readonly group = new THREE.Group();
  private avatars: Avatar[] = [];
  private loaded = false;

  private readonly forward = new THREE.Vector3();
  private readonly up = new THREE.Vector3();

  constructor(private sim: GrasshopperSim, private terrain: Terrain) {
    this.group.name = 'grasshoppers';

    // Pre-create empty avatar roots — load() fills them with the cloned GLB
    // as soon as it resolves.
    for (let i = 0; i < this.sim.hoppers.length; i++) {
      const root = new THREE.Group();
      root.name = `grasshopper-${i}`;
      root.matrixAutoUpdate = false;
      root.visible = false;
      const mixer = new THREE.AnimationMixer(root);
      this.avatars.push({ root, mixer, action: null });
      this.group.add(root);
    }
    void this.load();
  }

  private async load(): Promise<void> {
    let gltf;
    try {
      gltf = await new GLTFLoader().loadAsync(_assetUrl(MODEL_SLOT, MODEL_URL_DEFAULT));
    } catch (err) {
      console.warn('[Grasshopper] GLB load failed, keeping sphere fallback:', err);
      return;
    }

    const src = gltf.scene;
    src.updateMatrixWorld(true);

    // Stray-geometry cleanup was moved to the /insects page — edit the GLB
    // with the eraser tool there and drop the result back into
    // /public/assets/grasshopper.glb to replace this file.

    // Pull scale/rotation/offset from the /insects tuner store (shared with
    // the in-game render — edit in the viewer, refresh, instantly applied).
    const tune = readTune(TUNE_ID, TUNE_DEFAULTS);
    void HOPPER_LENGTH;

    const clip = gltf.animations[0] ?? null;

    for (let i = 0; i < this.avatars.length && i < this.sim.hoppers.length; i++) {
      try {
        const av = this.avatars[i];
        const cloned = skeletonClone(src);
        cloned.scale.setScalar(tune.scale);
        cloned.position.set(0, 0, 0);
        cloned.rotation.set(tune.rotX, tune.rotY, tune.rotZ);
        av.root.add(cloned);

        cloned.updateMatrixWorld(true);
        const feetBb = new THREE.Box3().setFromObject(cloned);
        cloned.position.y = -feetBb.min.y + tune.offY;
        cloned.updateMatrixWorld(true);

        cloned.traverse((o) => {
          const m = o as THREE.Mesh;
          if (!m.isMesh) return;
          m.castShadow = true;
          m.receiveShadow = true;
          m.frustumCulled = false;
        });

        const mixer = new THREE.AnimationMixer(cloned);
        let action: THREE.AnimationAction | null = null;
        if (clip) {
          action = mixer.clipAction(clip);
          action.play();
          action.time = Math.random() * clip.duration;
        }
        av.mixer = mixer;
        av.action = action;

      } catch (err) {
        console.error(`[Grasshopper] clone ${i} crashed:`, err);
      }
    }
    this.loaded = true;
  }

  update(dt: number, _elapsed: number): void {
    if (!this.loaded) return;
    const hoppers = this.sim.hoppers;
    for (let i = 0; i < hoppers.length; i++) {
      const g = hoppers[i];
      const av = this.avatars[i];
      if (!av) continue;
      if (!g.alive) {
        av.root.visible = false;
        continue;
      }
      av.root.visible = true;
      this.writePose(av.root, g);
      if (av.action) av.action.timeScale = g.task === 'graze' ? 0.6 : 2.2;
      av.mixer.update(dt);
    }
  }

  private writePose(root: THREE.Group, g: { x: number; y: number; z: number; heading: number; hopLift: number }): void {
    // Align to terrain normal so the grasshopper tilts with the slope.
    // hopLift is already baked into g.y by the sim.
    this.terrain.getNormal(g.x, g.z, this.up);
    this.forward.set(Math.cos(g.heading), 0, Math.sin(g.heading));
    this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    if (this.forward.lengthSq() < 1e-6) {
      this.forward.set(1, 0, 0);
      this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    }
    this.forward.normalize();

    const bx = this.forward.y * this.up.z - this.forward.z * this.up.y;
    const by = this.forward.z * this.up.x - this.forward.x * this.up.z;
    const bz = this.forward.x * this.up.y - this.forward.y * this.up.x;

    const e = root.matrix.elements;
    e[0] = this.forward.x; e[1] = this.forward.y; e[2] = this.forward.z; e[3] = 0;
    e[4] = this.up.x;      e[5] = this.up.y;      e[6] = this.up.z;      e[7] = 0;
    e[8] = bx;             e[9] = by;             e[10] = bz;            e[11] = 0;
    e[12] = g.x;           e[13] = g.y + 0.005;   e[14] = g.z;           e[15] = 1;
    root.matrixWorldNeedsUpdate = true;
  }
}
